/**
 * drawer-parts: the Carbon pieces the three /admin drawer bodies share
 * (OrgDrawerContent, PersonDrawerContent, TenantDrawerContent).
 *
 *   DrawerSection  a titled block (14px/600) under a border-subtle rule
 *   DetailGrid     a label/value description list, two columns from c-md
 *   ActionList     a flush list of full-width action rows, each a 600 label
 *                  over a 12px helper description; a danger row reads in
 *                  Coral ink (an irreversible or access-removing action)
 *   ActionFeedback the result line under an action: neutral text with a
 *                  check on success, an error InlineNotification on failure
 *   InlinePanel    a layer-02 sub-form inside the action list (Cancel, then
 *                  the confirm button)
 *
 * The drawer shell is SidePanel (via AdminDrawer): layer-01, a pinned header
 * and a body with 16px side padding. The action list bleeds to the panel's
 * edges so its rows read as one Carbon list.
 */
import { useId, type ReactNode } from 'react'
import clsx from 'clsx'

import { Button } from '@/components/carbon/Button'
import { InlineNotification } from '@/components/carbon/InlineNotification'
import { FIELD_ON_LAYER_02 } from '@/components/carbon/fieldStyles'
import { IconCheck } from '@/components/carbon/icons'

/* -------------------------------------------------------------------------- */
/* Sections and details                                                       */
/* -------------------------------------------------------------------------- */

export function DrawerSection({
  title,
  children,
  first = false,
  className,
}: {
  title: string
  children: ReactNode
  /** The drawer's first section: no rule above it. */
  first?: boolean
  className?: string
}) {
  const id = useId()
  return (
    <section aria-labelledby={id} className={clsx(!first && 'border-t border-line-subtle pt-4', className)}>
      <h3 id={id} className="m-0 mb-3 text-body font-semibold text-text-primary">
        {title}
      </h3>
      {children}
    </section>
  )
}

export interface DetailItem {
  label: string
  value: ReactNode
}

export function DetailGrid({ items, className }: { items: DetailItem[]; className?: string }) {
  return (
    <dl className={clsx('m-0 grid grid-cols-1 gap-x-6 gap-y-4 c-md:grid-cols-2', className)}>
      {items.map((item) => (
        <div key={item.label} className="min-w-0">
          <dt className="text-helper text-text-helper">{item.label}</dt>
          <dd className="m-0 mt-1 text-body text-text-primary [overflow-wrap:anywhere]">{item.value}</dd>
        </div>
      ))}
    </dl>
  )
}

/** Helper-tone text for a value that is absent or not recorded ("Never", "Not available yet"). */
export function Muted({ children }: { children: ReactNode }) {
  return <span className="text-text-helper">{children}</span>
}

/** A Microsoft ID, shortened, in the mono face with the full value as its title. */
export function MonoId({ value, short }: { value: string; short: string }) {
  return (
    <span className="font-mono text-helper" title={value}>
      {short}
    </span>
  )
}

/* -------------------------------------------------------------------------- */
/* Actions                                                                    */
/* -------------------------------------------------------------------------- */

/** The flush list the action rows sit in; it bleeds past the panel body's 16px padding. */
export function ActionList({ children }: { children: ReactNode }) {
  return <ul className="m-0 -mx-4 list-none border-t border-line-subtle p-0">{children}</ul>
}

export interface ActionRowProps {
  label: string
  description: string
  onClick?(): void
  disabled?: boolean
  /** Hover text, for an action that is not available yet. */
  title?: string
  /** An action that removes access or stops scanning: the label reads in Coral ink. */
  danger?: boolean
  /** A short state after the label ("Copied"). */
  trailing?: ReactNode
  /** The action's result, under the row. */
  feedback?: ActionResult | null
}

export interface ActionResult {
  tone: 'success' | 'error'
  message: string
}

export function ActionRow({ label, description, onClick, disabled, title, danger, trailing, feedback }: ActionRowProps) {
  const labelId = useId()
  const descId = useId()
  return (
    <li className="border-b border-line-subtle">
      <button
        type="button"
        onClick={onClick}
        disabled={disabled}
        title={title}
        aria-labelledby={labelId}
        aria-describedby={descId}
        className={clsx(
          'flex w-full flex-col items-start gap-0.5 px-4 py-3 text-left transition-colors duration-110 ease-carbon',
          disabled ? 'cursor-not-allowed' : 'hover:bg-layer-hover',
        )}
      >
        <span className="flex items-center gap-2">
          <span
            id={labelId}
            className={clsx(
              'text-body font-semibold',
              disabled ? 'text-text-placeholder' : danger ? 'text-crit-ink' : 'text-text-primary',
            )}
          >
            {label}
          </span>
          {trailing}
        </span>
        <span id={descId} className={clsx('text-helper', disabled ? 'text-text-placeholder' : 'text-text-helper')}>
          {description}
        </span>
      </button>
      {feedback ? <ActionFeedback result={feedback} className="px-4 pb-3" /> : null}
    </li>
  )
}

/** The outcome of an action: a neutral line with a check, or a blocking-error notification. */
export function ActionFeedback({ result, className }: { result: ActionResult; className?: string }) {
  if (result.tone === 'error') {
    return (
      <div className={className}>
        <InlineNotification kind="error" title={result.message} />
      </div>
    )
  }
  return (
    <p role="status" className={clsx('m-0 flex items-start gap-2 text-helper text-text-secondary', className)}>
      <IconCheck size={16} className="mt-px flex-none text-text-primary" />
      <span className="min-w-0">{result.message}</span>
    </p>
  )
}

/** An inline sub-form in the action list: heading, body, then Cancel and the confirm button. */
export function InlinePanel({
  heading,
  children,
  primaryLabel,
  primaryDisabled,
  primaryLoading,
  onPrimary,
  onCancel,
  cancelDisabled,
}: {
  heading: string
  children: ReactNode
  primaryLabel: string
  primaryDisabled?: boolean
  primaryLoading?: boolean
  onPrimary(): void
  onCancel(): void
  cancelDisabled?: boolean
}) {
  const id = useId()
  return (
    <li role="group" aria-labelledby={id} className={clsx('border-b border-line-subtle bg-layer-2 px-4 py-4', FIELD_ON_LAYER_02)}>
      <p id={id} className="m-0 mb-3 text-body font-semibold text-text-primary">
        {heading}
      </p>
      <div>{children}</div>
      <div className="mt-4 grid grid-cols-2 gap-px">
        <Button kind="secondary" size="md" fullWidth onClick={onCancel} disabled={cancelDisabled}>
          Cancel
        </Button>
        <Button kind="primary" size="md" fullWidth onClick={onPrimary} disabled={primaryDisabled} loading={primaryLoading}>
          {primaryLabel}
        </Button>
      </div>
    </li>
  )
}
